import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EncryptionService } from './encryption.service';
import { Router, NavigationStart, Event as NavigationEvent } from '@angular/router';
import { Location } from '@angular/common';

export enum UserRole {
  ADMIN = 'admin',
  NGO = 'ngo',
  CORPORATE = 'corporate',
  VOLUNTEER = 'volunteer'
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  organization_id?: string;
  onboarding_completed?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();
  private isInitialized = false;

  constructor(
    private readonly encryptionService: EncryptionService,
    private readonly router: Router,
    private readonly location: Location
  ) {
    // Check localStorage for user info on initialization
    
    // Handle browser navigation events
    this.setupNavigationHandlers();
  }

  public async init(): Promise<void> {
    await this.loadUserFromStorage();
  }

  public async loadUserFromStorage(): Promise<void> {
    if (this.isInitialized) return;

    const encryptedUserInfo = localStorage.getItem('user_info');
    if (encryptedUserInfo) {
      try {
        // First try to parse as JSON (for mock user)
        try {
          const mockUser = JSON.parse(encryptedUserInfo);
          if (mockUser?.role) {
            this.currentUserSubject.next(mockUser);
            this.isInitialized = true;
            return;
          }
        } catch (jsonError) {
          console.error('Not a JSON user, trying to decrypt...');
        }

        // If not JSON, try to decrypt
        const user = await this.encryptionService.decrypt(encryptedUserInfo);
        if (user?.role) {
          this.currentUserSubject.next(user);
          this.isInitialized = true;
        } else {
          console.error('Invalid user info in localStorage');
          this.clearUserData();
        }
      } catch (e) {
        console.error('Error decrypting user info from localStorage:', e);
        this.clearUserData();
      }
    } else {
      console.error('No user info found in localStorage');
      this.isInitialized = true;
    }
  }

  private clearUserData(): void {
    this.currentUserSubject.next(null);
    localStorage.removeItem('user_info');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  private handleInvalidUser(): void {
    this.clearUserData();
    // Only redirect if we're not already on the login page
    if (!this.router.url.startsWith('/login')) {
      this.router.navigate(['/login']);
    }
  }
  
  /**
   * Sets up navigation event handlers to prevent unauthorized navigation
   * and handle browser back button
   */
  private setupNavigationHandlers(): void {
    // Subscribe to router events
    this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationStart) {
        const currentUser = this.getCurrentUser();
        const url = event.url;
        
        // If user is logged in and trying to access login page, redirect to appropriate dashboard
        if (currentUser && url.startsWith('/login')) {
          // We can't prevent the navigation directly, but we can redirect immediately after
          setTimeout(() => {
            if (this.router.url.startsWith('/login')) {
              this.redirectToDashboard(currentUser);
            }
          }, 0);
        }
        
        // Prevent navigation to external URLs by checking if it's an absolute URL
        // that doesn't include our hostname
        if (url.match(/^https?:\/\//i) && !url.includes(window.location.hostname)) {
          console.warn('Navigation to external URL prevented:', url);
          // Stay on current page
          setTimeout(() => {
            this.location.back();
          }, 0);
        }
      }
    });
    
    // Handle browser back button
    window.addEventListener('popstate', () => {
      const currentUser = this.getCurrentUser();
      
      // If user is logged in and back button would take them to login page
      // redirect to dashboard
      if (currentUser && window.location.pathname.startsWith('/login')) {
        setTimeout(() => {
          this.redirectToDashboard(currentUser);
        }, 0);
      }
    });
  }
  
  /**
   * Redirects user to appropriate dashboard based on role
   */
  public redirectToDashboard(user: User): void {
    switch (user.role) {
      case UserRole.ADMIN:
        this.router.navigate(['/user/dashboard']);
        break;
      case UserRole.NGO:
        if (user.onboarding_completed) {
          this.router.navigate(['/ngo/dashboard']);
        } else {
          this.router.navigate(['/ngo/onboarding']);
        }
        break;
      case UserRole.CORPORATE:
        if (user.onboarding_completed) {
          this.router.navigate(['/corporate/dashboard']);
        } else {
          this.router.navigate(['/corporate/onboarding']);
        }
        break;
      case UserRole.VOLUNTEER:
        this.router.navigate(['/volunteer/dashboard']);
        break;
      default:
        this.router.navigate(['/']);
    }
  }

  getCurrentUser(): User | null {
    const user = this.currentUserSubject.value;
    return user;
  }

  getUserRole(): UserRole | null {
    return this.currentUserSubject.value?.role || null;
  }

  async logout(): Promise<void> {
    // Set a flag in sessionStorage to indicate intentional logout
    // This will be used by NavigationService to allow navigation to login page
    sessionStorage.setItem('intentional_logout', 'true');
    
    // Clear user data (tokens and user info)
    this.clearUserData();
    
    // Clear browser history to prevent back button issues
    window.history.pushState(null, '', window.location.href);
    window.history.pushState(null, '', window.location.href);
    
    // Navigate to login page
    this.router.navigate(['/landing']).then(() => {
      // Remove the intentional logout flag after navigation completes
      setTimeout(() => {
        sessionStorage.removeItem('intentional_logout');
      }, 1000);
    });
  }

  // Public method to update the current user
  public updateCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    // Also update the encrypted user info in localStorage
    this.encryptionService.encrypt(user).then(encryptedUser => {
      localStorage.setItem('user_info', encryptedUser);
    }).catch(error => {
      console.error('Error encrypting user info during update:', error);
    });
  }
}
